import { Stack } from "@mui/material";
import type { JSX } from "react";
import { useCallback } from "react";
import type {
  CalculationType,
  FieldConfiguration,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionField, MeasureField } from "../../../../../../../../../api/types/svTypes.ts";
import { isEligibleDateScopeField } from "../../../../../utilities/fieldsHelper.ts";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../../viewer/common/fields/FieldOptionsDivider.tsx";
import CustomId from "../../../api-report/editor/fields/CustomId.tsx";
import { isNumericField } from "../../../utils/isNumericField.ts";
import { isGlTableMeasure, isSharesRelatedMeasure } from "../../../utils/MeasureUtils.ts";
import type { DateCalculationType } from "../custom-field/utilities/dateCalculationTypes.ts";
import AmountTypeMenuItem from "../field-options/AmountTypeMenuItem.tsx";
import CalculationFieldMenuItem from "../field-options/CalculationFieldMenuItem.tsx";
import CashFlowFrequencyMenuItem from "../field-options/CashFlowFrequencyMenuItem.tsx";
import ChartOfAccountsItem from "../field-options/ChartOfAccountsItem.tsx";
import ExplainMenuItem from "../field-options/ExplainMenuItem.tsx";
import PeriodMenuItem from "../field-options/PeriodMenuItem.tsx";
import TransferFieldMenuItem from "../field-options/TransferFieldMenuItem.tsx";
import ValueFieldsMenuItem from "../field-options/ValueFieldsMenuItem.tsx";
import CashFlowDateFieldMenuItem from "./CashFlowDateFieldMenuItem.tsx";

interface Props {
  field: MeasureField;
  dimensionFields: DimensionField[];
  conditions: ConditionField[];
  otherOptions: JSX.Element;
  isApiReport?: boolean;
  canAddCustomField: boolean | undefined;
  cancel: () => void;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onRemove: () => void;
  onCalculationItemClick: ((calculationType: CalculationType) => void) | undefined;
  onDateCalculationItemClick?: (type: DateCalculationType) => void;
  readonly: boolean;
}

export default function MeasureFieldOptions({
  field,
  dimensionFields,
  conditions,
  otherOptions,
  cancel,
  saveChanges,
  onRemove,
  isApiReport = false,
  onCalculationItemClick,
  onDateCalculationItemClick,
  canAddCustomField,
  readonly,
}: Props) {
  const updateCustomLabel = useCallback(
    (customLabel: string | undefined) => {
      saveChanges({ customLabel }, false);
    },
    [saveChanges]
  );

  const updateCustomId = useCallback(
    (customId: string | undefined) => {
      saveChanges({ customId }, false);
    },
    [saveChanges]
  );

  const handleRemove = useCallback(() => {
    onRemove();
    cancel();
  }, [cancel, onRemove]);

  const amountTypeChangeDisabled = !isGlTableMeasure(field.meta);
  const isSharesMeasure = isSharesRelatedMeasure(field);
  const showAmountTypeField =
    !isSharesMeasure && field.meta.capabilities.isConfigurable && field.meta.capabilities.changeAmountField;
  const showCalculatedByField =
    !isSharesMeasure && field.meta.capabilities.isConfigurable && field.meta.capabilities.changeCalculatedBy;
  const isScalarMeasure = field.meta.capabilities.isScalar;

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={updateCustomLabel}
        readonly={readonly}
      />
      {isApiReport && (
        <CustomId
          customId={field.config.customId}
          defaultId={field.config.guid}
          updateCustomId={updateCustomId}
          readonly={readonly}
        />
      )}
      <FieldOptionsDivider />
      {isNumericField(field) && canAddCustomField && !isScalarMeasure && !readonly && (
        <CalculationFieldMenuItem
          onCalculationItemClick={(calculationType) => onCalculationItemClick?.(calculationType)}
          onDateCalculationItemClick={isEligibleDateScopeField(field) ? onDateCalculationItemClick : undefined}
          menuItemSx={{ mt: 1 }}
        />
      )}
      <ExplainMenuItem measure={field} onClose={cancel} />
      {field.meta.capabilities.isConfigurable && (
        <>
          {field.meta.capabilities.chartOfAccountsAllowed && <ChartOfAccountsItem measure={field} onClose={cancel} />}
          <FieldOptionsDivider />
          <Stack
            spacing={1.5}
            useFlexGap
            sx={{
              width: "100%",
              flex: 1,
              px: 2,
              py: 1.5,
            }}
          >
            {field.meta.capabilities.dateRangeCustomization && (
              <>
                <CashFlowDateFieldMenuItem
                  measure={field}
                  fields={dimensionFields}
                  conditions={conditions}
                  saveChanges={saveChanges}
                  readonly={readonly}
                />
                <PeriodMenuItem measure={field} saveChanges={saveChanges} onClose={cancel} readonly={readonly} />
              </>
            )}
            {field.meta.capabilities.transferredBalanceAllowed && (
              <TransferFieldMenuItem measure={field} onUpdate={saveChanges} readonly={readonly} />
            )}
            {showAmountTypeField && (
              <AmountTypeMenuItem
                measure={field}
                saveChanges={saveChanges}
                disabled={amountTypeChangeDisabled || readonly}
              />
            )}
            {showCalculatedByField && (
              <ValueFieldsMenuItem measure={field} saveChanges={saveChanges} readonly={readonly} />
            )}
            <CashFlowFrequencyMenuItem measure={field} saveChanges={saveChanges} onClose={cancel} readonly={readonly} />
          </Stack>
          <FieldOptionsDivider />
        </>
      )}
      {otherOptions}
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
}
